<template>
  <div class="content">
    <div class="content-wrap">
      <h3>{{ t('add-user').toUpperCase() }}</h3>
      <div class="card-body">
        <!-- VeeValidate formularz z walidacją -->
        <Form
          ref="form"
          @submit="onSubmit"
          :validation-schema="schema"
          v-slot="{ errors, isSubmitting }"
          :keepValues="false"
        >
          <!-- Główna sekcja formularza, 2 kolumny -->
          <div class="column d-flex justify-content-center user-data">
            <!-- Kolumna 1 -->
            <div class="col-sm-6">
              <!-- Login użytkownika -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('username') }}:</label>
                <div class="col-sm-8">
                  <Field name="userName" type="text" class="form-control" :class="{ 'is-invalid': errors.userName }" />
                  <div class="invalid-feedback">{{ errors.userName }}</div>
                </div>
              </div>
              <!-- Hasło użytkownika -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('password') }}:</label>
                <div class="col-sm-8">
                  <Field name="password" type="password" class="form-control" v-model="form.password" :class="{ 'is-invalid': errors.password }" />
                  <div class="invalid-feedback">{{ errors.password }}</div>
                </div>
              </div>
              <!-- Powtórz hasło -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('confirm-password') }}:</label>
                <div class="col-sm-8">
                  <Field name="password2" type="password" class="form-control" v-model="form.password2" :class="{ 'is-invalid': errors.password2 }" />
                  <div class="invalid-feedback">{{ errors.password2 }}</div>
                </div>
              </div>
              <!-- Wizualizacja siły hasła -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('password-strength-level') }}:</label>
                <div class="col-sm-8">
                  <password-meter :password="form.password" />
                  <div class="d-flex password-meter justify-content-center center-aligned">
                    <div class="col-sm-4">{{ t('password-strength-weak') }}</div>
                    <div class="col-sm-4">{{ t('password-strength-medium') }}</div>
                    <div class="col-sm-4">{{ t('password-strength-strong') }}</div>
                  </div>
                </div>
              </div>
              <!-- Telefon użytkownika -->
              <div class="form-group row phone">
                <label class="col-sm-4 col-form-label">{{ t('phone-number') }}:</label>
                <div class="col-sm-8 d-flex flex-row">
                  <!-- Prefiks +48 (zablokowany) -->
                  <input type="text" class="form-control" value="+48" disabled style="width: 50px; text-align: center; margin-right: 5px;" />
                  <!-- Numer telefonu -->
                  <Field name="phoneNumber" type="text" class="form-control phone-number" :class="{ 'is-invalid': errors.phoneNumber }" />
                </div>
                <div class="invalid-feedback phone-invalid">{{ errors.phoneNumber }}</div>
              </div>
            </div>
            <!-- Kolumna 2 -->
            <div class="col-sm-6">
              <!-- Uprawnienia -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('permissions') }}:</label>
                <div class="col-sm-8">
                  <Field name="roles" as="select" class="form-control" :class="{ 'is-invalid': errors.roles }">
                    <option value="" disabled>{{ t('select-role') }}</option>
                    <!--
                      Admin bez ROLE_17/18/19 nie zobaczy grup zawierających te uprawnienia
                      — żeby nie dało się ich nadać przez wybór gotowej grupy.
                    -->
                    <option v-for="role in assignableRoles" :key="role.id" :value="role.id">
                      {{ tdb(role.name) }}
                    </option>
                  </Field>
                  <div class="invalid-feedback">{{ errors.roles }}</div>
                </div>
              </div>
              <!-- Imię -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('name') }}:</label>
                <div class="col-sm-8">
                  <Field name="firstName" type="text" class="form-control" :class="{ 'is-invalid': errors.firstName }" />
                  <div class="invalid-feedback">{{ errors.firstName }}</div>
                </div>
              </div>
              <!-- Nazwisko -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('lastname') }}:</label>
                <div class="col-sm-8">
                  <Field name="lastName" type="text" class="form-control" :class="{ 'is-invalid': errors.lastName }" />
                  <div class="invalid-feedback">{{ errors.lastName }}</div>
                </div>
              </div>
              <!-- E-mail -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('email-address') }}:</label>
                <div class="col-sm-8">
                  <Field name="email" type="text" class="form-control" :class="{ 'is-invalid': errors.email }" />
                  <div class="invalid-feedback">{{ errors.email }}</div>
                </div>
              </div>
              <!-- Opis użytkownika -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('description') }}:</label>
                <div class="col-sm-8">
                  <Field name="description" type="textarea" class="form-control" />
                  <div class="invalid-feedback">{{ errors.description }}</div>
                </div>
              </div>
              <!-- Status użytkownika (checkbox) -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('status') }}:</label>
                <div class="col-sm-8">
                  <Field name="status" type="checkbox" :value="true" />
                  <div class="invalid-feedback">{{ errors.status }}</div>
                </div>
              </div>
              <!-- Grupy kontaktów listy poszukiwanych. Po utworzeniu konta użytkownik zostanie
                   dodany do zaznaczonych grup jako kontakt (e-mail) → widoczność + powiadomienia. -->
              <div class="form-group row" v-if="contactGroupsAvailable.length > 0">
                <label class="col-sm-4 col-form-label">{{ t('user-contact-groups') }}:</label>
                <div class="col-sm-8">
                  <div class="cg-checklist">
                    <label
                      v-for="group in contactGroupsAvailable"
                      :key="group.id"
                      class="cg-item"
                    >
                      <input
                        class="cg-checkbox"
                        type="checkbox"
                        :value="group.id"
                        v-model="selectedContactGroupIds"
                      />
                      <span class="cg-name">{{ group.name }}</span>
                    </label>
                  </div>
                  <small class="form-text text-muted">{{ t('user-contact-groups-hint') }}</small>
                </div>
              </div>
              <!-- Kanał, jakim zostanie założony kontakt użytkownika (tylko gdy wybrano grupy). -->
              <div class="form-group row" v-if="contactGroupsAvailable.length > 0 && selectedContactGroupIds.length > 0">
                <label class="col-sm-4 col-form-label">{{ t('user-contact-groups-channel') }}:</label>
                <div class="col-sm-8 cg-channel">
                  <label class="cg-radio-item">
                    <input class="cg-radio" type="radio" :value="0" v-model.number="wantedContactType" />
                    <span>{{ t('email') }}</span>
                  </label>
                  <label class="cg-radio-item">
                    <input class="cg-radio" type="radio" :value="1" v-model.number="wantedContactType" />
                    <span>{{ t('contact-type-sms') }}</span>
                  </label>
                  <label class="cg-radio-item">
                    <input class="cg-radio" type="radio" :value="2" v-model.number="wantedContactType" />
                    <span>{{ t('contact-type-mms') }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <!-- Propozycja hasła + generowanie -->
          <div class="column d-flex justify-content-center">
            <div class="form-group col">
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('password-proposal') }}:</label>
                <div class="col-sm-8">
                  <Field name="proposed" type="text" class="form-control" v-model="form.proposed" />
                </div>
              </div>
            </div>
            <div class="col generate-pass">
              <div class="row">
                <div class="col-sm-4">
                  <button type="button" @click="generatePassword" class="btn button-add-user generate">
                    {{ t('generate').toUpperCase() }}
                  </button>
                </div>
                <div class="col-sm-8">
                  <button type="button" @click="usePassword" class="btn button-add-user use-password" :disabled="!form.proposed">
                    {{ t('use-suggested-password') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <!-- Obsługa komunikatów o błędach i sukcesach -->
          <div class="form-group row d-flex flex-column align-items-center process">
            <div class="col-auto" v-if="processError">
              <div class="process-error form-control">{{ processError }}</div>
            </div>
            <div class="col-auto" v-if="processSuccess">
              <div class="process-success form-control">{{ processSuccess }}</div>
            </div>
            <div class="col-auto">
              <button type="button" @click="goBack" class="btn button-add-user">
                {{ t('cancel') }}
              </button>
              <button class="btn button-save" :disabled="isSubmitting">
                <span v-show="isSubmitting" class="spinner-border spinner-border-sm mr-1"></span>
                {{ t('save').toUpperCase() }}
              </button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>
</template>

<script setup>
import moment from "moment";
import {Field, Form} from "vee-validate";
import * as Yup from "yup";

moment.locale("pl");

// Import tłumaczeń
const { t } = useI18n();

// Definicja reguł walidacji formularza (VeeValidate/Yup)
const schema = Yup.object().shape({
  userName: Yup.string().required(t('user-username-required')),
  password: Yup.string().required(t('user-password-required')),
  password2: Yup.string().required(t('user-password-required')),
  phoneNumber: Yup.string().required(t('user-phone-required')),
  firstName: Yup.string().required(t('user-name-required')),
  lastName: Yup.string().required(t('user-lastname-required')),
  email: Yup.string()
    .required(t('user-email-required'))
    .email(t('incorrect-email-address')),
  roles: Yup.string().required(t('user-permissions-required')),
  description: Yup.string(),
  status: Yup.bool(),
  ticket: Yup.bool(),
});
</script>

<script>
import PasswordMeter from "vue-simple-password-meter";
import Users from "@/services/Users";
import ContactGroupsService from "@/services/ContactGroupsService";
import { useI18n } from 'vue-i18n';
import { useUserStore } from "@/stores/user.store";

export default {
  components: {
    PasswordMeter,
  },
  name: "AddUser",
  computed: {
    // Pobranie store'a przez getter — w SFC z `<script setup>` druga sekcja `<script>`
    // (Options API) nie ma uruchamianego `setup()`, więc nie da się zwrócić store'a
    // przez `setup() { return { userStore } }`. Computed wywoła `useUserStore()` przy
    // każdym dostępie (singleton, zero kosztu).
    userStore() {
      return useUserStore();
    },
    // Grupy ról, które admin może przypisać nowo dodawanemu użytkownikowi. Wyklucza
    // grupy zawierające uprawnienia chronione (ROLE_17/18/19), jeśli admin sam ich nie ma —
    // może wtedy nadawać tylko grupy z uprawnieniami operatorskimi.
    assignableRoles() {
      return (this.rolesAvailable || []).filter(
        (role) => this.userStore.canAssignRoleGroup(role)
      );
    },
  },
  data() {
    return {
      // Stany i dane formularza
      loading: false,
      users: [],
      rolesAvailable: [],
      contactGroupsAvailable: [],
      selectedContactGroupIds: [],
      wantedContactType: 0, // 0 = e-mail, 1 = SMS (nr tel.) — kanał kontaktu przy przypisaniu do grup
      error: null,
      moment: moment,
      getPasswordLength: 14,
      processError: false,
      processSuccess: false,
      form: {
        userName: "",
        password: "",
        password2: "",
        phoneNumber: "",
        proposed: "",
        firstName: "",
        lastName: "",
        email: "",
        roles: "",
        description: "",
        status: false,
        ticket: false,
        organizationName: "",
      },
      organizationsAvailable: [],
    };
  },
  created() {
    this.fetchRoles(); // Pobierz dostępne role
    this.fetchContactGroups(); // Pobierz grupy kontaktów do przypisania
  },
  methods: {
    // Powrót do poprzedniego widoku
    goBack() {
      this.$emit("cancel-add-user");
    },
    // Pobierz listę ról użytkowników z API
    fetchRoles() {
      Users.getRolesList()
        .then((roles) => {
          this.rolesAvailable = roles;
        })
        .catch((error) => {
          console.error("Error fetching roles:", error);
        });
    },
    // Pobierz listę grup kontaktów, do których można od razu przypisać zakładanego użytkownika.
    // Gdy operator nie ma dostępu do grup (brak ROLE_19/wanted), lista pozostaje pusta,
    // a sekcja wyboru grup jest ukryta — przypisanie robi się wtedy w panelu Kontakty.
    fetchContactGroups() {
      ContactGroupsService.getAll()
        .then((groups) => {
          this.contactGroupsAvailable = Array.isArray(groups) ? groups : [];
        })
        .catch((error) => {
          console.error("Error fetching contact groups:", error);
          this.contactGroupsAvailable = [];
        });
    },
    // Generuj losowe hasło
    generatePassword() {
      const digits = "1234567890";
      const alphabet = "1234567890-=!@#$%^&*()_+qazwsxedcrfvtgbyhnujmik,ol.p;/[]QAZWSXEDCRFVTGBYHNUJMIKOL>P:?{'}|";
      
      let pass = "";
      pass += digits[Math.floor(Math.random() * digits.length)];
      for (let i = 1; i < this.getPasswordLength; i++) {
        pass += alphabet[Math.floor(Math.random() * alphabet.length)];
      }
      pass = pass.split("").sort(() => 0.5 - Math.random()).join("");
      this.form.proposed = pass;
      return pass;
    },
    // Użyj wygenerowanego hasła w polach formularza
    usePassword() {
      this.form.password = this.form.proposed;
      this.form.password2 = this.form.proposed;
    },
    // Resetuj stan formularza (po sukcesie)
    resetForm() {
      this.form = {
        userName: "",
        password: "",
        password2: "",
        phoneNumber: "",
        proposed: "",
        firstName: "",
        lastName: "",
        email: "",
        roles: "",
        description: "",
        status: false,
        ticket: false,
      };
      if (this.$refs.form) {
        this.$refs.form.resetForm();
      }
    },
    // Obsługa submit formularza
    onSubmit(values) {
      this.processSuccess = null;
      this.processError = null;

      const roleId = Number(values.roles);
      const selectedRole = this.rolesAvailable.find(r => Number(r.id) === roleId);

      if (!selectedRole) {
        this.processError = "Nie znaleziono roli (sprawdź typy id).";
        return;
      }

      // Walidacja admina (rola 8) — poprawiona, bez JSON.stringify
      if ((roleId === 8 || roleId === 13) && values.password.length < 12) {
        this.processError = this.$t('password-for-admin-cannot-be-shorter-than-12');
        return;
      }

      const sendUser = {
        username: values.userName,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: `+48${values.phoneNumber}`,
        roles: [roleId],
        enabled: !!values.status,
        ticket: !!values.ticket,
        email: values.email,
        description: values.description,
        sendUserLogsToServer: true,
        wantedContactGroupIds: this.selectedContactGroupIds.map(Number),
        wantedContactType: Number(this.wantedContactType),
      };

      Users.addUser(sendUser)
          .then(() => {
            this.processSuccess = "Dodano użytkownika";
            this.$refs.form?.resetForm();
            this.$emit('user-added');
            this.$router.push('/user-management');
          })
          .catch((error) => {
            console.log(error);
            this.processError = error;
          });
    },
  },
};
</script>

<style scoped>

.col-form-label {
  text-align: right;
}
.cg-checklist {
  max-height: 170px;
  overflow-y: auto;
  border: 1px solid var(--border-default);
  border-radius: 0.375rem;
  padding: 0.25rem;
  background: var(--surface-primary);
}
.cg-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  padding: 0.35rem 0.5rem;
  border-radius: 0.25rem;
  cursor: pointer;
  font-weight: 400;
  text-align: left;
}
.cg-item:hover {
  background: var(--surface-secondary);
}
.cg-checkbox {
  -webkit-appearance: auto;
  appearance: auto;
  width: 16px;
  height: 16px;
  min-width: 16px;
  margin: 0;
  padding: 0;
  border-radius: 3px;
  accent-color: var(--status-ok);
  cursor: pointer;
  flex: 0 0 auto;
}
.cg-name {
  line-height: 1.2;
  word-break: break-word;
}
.cg-channel {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.25rem;
  padding-top: 0.35rem;
}
.cg-radio-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  font-weight: 400;
  cursor: pointer;
}
.cg-radio {
  -webkit-appearance: auto;
  appearance: auto;
  width: 16px;
  height: 16px;
  min-width: 16px;
  margin: 0;
  padding: 0;
  accent-color: var(--status-ok);
  cursor: pointer;
  flex: 0 0 auto;
}
.process-error {
  color: darkred;
}

.process-success {
  color: var(--status-ok);
}

.content {
  width: 100%;
  min-height: 270px;
  margin-bottom: 20px;
}

.password-meter {
  font-size: var(--font-size-sm);
}

h3 { 
  padding: 15px 25px;
  background-color: var(--surface-primary);
}

.card-body .column {
  padding: 20px 30px;
}

.card-body .column > div {
  padding: 5px 20px;
}

.user-data {
  border-bottom: 1px solid var(--border-default);
}

.invalid-feedback {
  font-size: var(--font-size-xs);
}

input[type="checkbox"] {
  margin-top: 13px;
}

.cg-checkbox {
  margin-top: 0 !important;
}

.generate-pass {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.button-add-user {
  margin-bottom: 1rem;
  padding: 0.5rem 2rem 0.5rem 2rem;
  margin-right: 0.5rem;
}

.button-save {
  margin-bottom: 1rem;
  padding: 0.5rem 2rem 0.5rem 2rem;
  border: 1px solid var(--status-ok);
}

.generate {
  padding: .75rem 2rem .75rem 2rem;
}

/* „Użyj proponowanego hasła" ma mieć dokładnie wysokość „Generuj". Wcześniej
   etykieta (218 px w jednej linii) nie mieściła się w przycisku (275 px minus
   2 × 2rem paddingu = 209 px na treść), łamała się na dwie linie i przycisk
   rósł do ~58 px przy 42 px „Generuj".

   Węższy padding poziomy (1 rem → 241 px na treść) mieści etykietę w jednej
   linii z zapasem, a `nowrap` gwarantuje, że już nigdy się nie złamie.
   Pionowy padding i stopień pisma zostają takie jak w „Generuj", więc oba
   przyciski są równej wysokości bez podawania jej wprost. */
.use-password {
  padding: .75rem 1rem;
  white-space: nowrap;
}

.phone {
  margin-top: 1.7rem;
}

.process-success {
  margin-bottom: 1rem;
}

.process-error {
  margin-bottom: 1rem;
}

.phone-invalid {
  text-align: end;
}

</style>
